/**
 * Centralized configuration for scroll-to-slide prevention logic
 * This replaces the scattered ad-hoc checks throughout the codebase
 */

// Configuration for elements that should prevent scroll-to-slide
export const SCROLL_PREVENTION_CONFIG = {
  // Form elements that should always prevent scroll-to-slide
  formElements: ['INPUT', 'TEXTAREA', 'SELECT'],

  // Editor-related selectors and attributes
  editors: {
    contentEditable: { attribute: 'contenteditable', value: 'true' },
    tinyMCEClasses: ['mce-edit-focus', 'mce-content-body', 'mce-editor'],
  },

  // Custom selectors for specific elements that should prevent scroll
  customSelectors: [
    // Generic class for manually marking elements that should prevent scroll-to-slide
    '.prevent-scroll-to-slide',
  ],

  // Special container IDs that need custom handling
  specialContainers: {
    'canvas-wrapper': {
      checkScrollableChildren: true,
      maxDepth: 21,
    },
  },

  // Areas where scroll-to-slide should be ALLOWED (whitelist approach)
  // If this list is not empty, scroll-to-slide will ONLY work in these areas
  allowedAreas: [
    // Empty by default - means scroll-to-slide works everywhere except prevented areas
    // To switch to whitelist mode, add selectors here:
    // '#middle-slide-intro',
    // '.slide-content-area'
  ],
}

// Configuration for scroll detection
export const SCROLL_DETECTION_CONFIG = {
  throttleTime: 200,
  minDeltaY: 7,
  maxDepth: 21,
  maxChildrenDepth: 15,
}

/**
 * Check if an element is a form element based on configuration
 */
export const isConfiguredFormElement = (element) => {
  return SCROLL_PREVENTION_CONFIG.formElements.includes(element.tagName)
}

/**
 * Check if an element is an editor based on configuration
 */
export const isConfiguredEditor = (element) => {
  const { editors } = SCROLL_PREVENTION_CONFIG

  // Check contenteditable
  if (element.getAttribute &&
      element.getAttribute(editors.contentEditable.attribute) === editors.contentEditable.value) {
    return true
  }

  // Check TinyMCE classes
  if (element.classList) {
    const hasTinyMCEClass = editors.tinyMCEClasses.some((className) => element.classList.contains(className))
    if (hasTinyMCEClass) {
      return true
    }
  }

  return false
}

/**
 * Check if element matches any custom prevention selectors
 */
export const matchesPreventionSelector = (element) => {
  return SCROLL_PREVENTION_CONFIG.customSelectors.some((selector) => {
    try {
      if (element.matches && element.matches(selector)) {
        return true
      }
      // Also check if any parent matches (for nested elements)
      if (element.closest && element.closest(selector)) {
        return true
      }
      return false
    } catch (e) {
      // Invalid selector, skip
      return false
    }
  })
}

/**
 * Check if scroll-to-slide is allowed in the current area (whitelist mode)
 */
export const isInAllowedArea = (element, container) => {
  const { allowedAreas } = SCROLL_PREVENTION_CONFIG

  // If no allowed areas specified, allow everywhere (blacklist mode)
  if (allowedAreas.length === 0) {
    return true
  }

  // Helper function to check if an element matches any allowed area
  const elementMatchesAllowedArea = (el) => {
    return allowedAreas.some((selector) => {
      try {
        return el.matches && el.matches(selector)
      } catch (e) {
        return false
      }
    })
  }

  // Check if element or its parents are in allowed areas
  let current = element
  while (current && current !== container) {
    if (elementMatchesAllowedArea(current)) {
      return true
    }
    current = current.parentElement
  }

  return false
}

/**
 * Get configuration for special containers
 */
export const getSpecialContainerConfig = (containerId) => {
  return SCROLL_PREVENTION_CONFIG.specialContainers[containerId] || null
}

/**
 * Add a new form element type to the configuration
 */
export const addFormElementType = (tagName) => {
  if (!SCROLL_PREVENTION_CONFIG.formElements.includes(tagName)) {
    SCROLL_PREVENTION_CONFIG.formElements.push(tagName)
  }
}

/**
 * Add a new custom selector for scroll prevention
 */
export const addPreventionSelector = (selector) => {
  if (!SCROLL_PREVENTION_CONFIG.customSelectors.includes(selector)) {
    SCROLL_PREVENTION_CONFIG.customSelectors.push(selector)
  }
}

/**
 * Add an allowed area selector (whitelist mode)
 */
export const addAllowedArea = (selector) => {
  if (!SCROLL_PREVENTION_CONFIG.allowedAreas.includes(selector)) {
    SCROLL_PREVENTION_CONFIG.allowedAreas.push(selector)
  }
}

/**
 * Configure editor types
 */
export const addEditorConfig = (key, config) => {
  SCROLL_PREVENTION_CONFIG.editors[key] = config
}

/**
 * Easy way to add common UI components that should prevent scroll
 */
export const addUIComponentPrevention = (componentSelectors) => {
  SCROLL_PREVENTION_CONFIG.customSelectors.push(...componentSelectors)
}

// Helper to debug current configuration
export const debugScrollPrevention = () => {
  // Debug function available but does not output to console to avoid linter warnings
  return {
    config: SCROLL_PREVENTION_CONFIG,
    detection: SCROLL_DETECTION_CONFIG,
  }
}
